import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Patient } from "@/types";
import { Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePatientsStore } from "@/lib/stores/patients";

interface PatientServicesProps {
  patient: Patient;
  onServiceRemoved?: () => void;
}

export function PatientServices({ patient, onServiceRemoved }: PatientServicesProps) {
  const { toast } = useToast();
  const { removePatientService } = usePatientsStore();

  if (!patient?.services || patient.services.length === 0) {
    return null;
  }

  const handleDeleteService = async (serviceId: string, serviceName: string) => {
    try {
      await removePatientService(patient.id, serviceId);
      
      toast({
        title: "Service removed",
        description: `${serviceName} has been removed from patient services.`,
      });

      // Callback to refresh parent component
      if (onServiceRemoved) {
        onServiceRemoved();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove service. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Services</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            {patient.services?.map((service) => (
              <div key={service.id} className="flex items-center justify-between p-2 border rounded-lg">
                <div>
                  <div className="font-medium">{service.service_name}</div>
                  <div className="text-sm text-muted-foreground">₹{service.price}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteService(service.id, service.service_name)}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                  title="Remove service"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2 border-t">
            <p className="font-semibold">
              Total: ₹{patient?.services?.reduce((total, service) => total + service.price, 0).toFixed(2)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
